import React from 'react';
import { useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Grid,
  createStyles,
  Theme,
  makeStyles,
} from '@material-ui/core';
import classNames from 'classnames';
import { RootState } from '../../store';
import NavbarContent, { NavbarContentProps } from './NavbarContent';
import { drawerWidth } from '../Sidebar/DrawerSection';
import { Banner } from '../Banner';
import {
  BANNER_HEIGHT,
  DESKTOP_APP_BAR_HEIGHT,
  MOBILE_APP_BAR_HEIGHT,
} from '../../constants/uiConsts';
import { DividersAndCardBorders } from '../../theme/colors';
import { ChannelsPageLayoutHeaderContext } from '../Layout';

interface NavBarStyleProps {
  showBanner: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
      boxShadow: 'none',
      zIndex: theme.zIndex.drawer + 1,
      left: '50%',
      transform: 'translateX(-50%)',
      borderBottom: `1px solid ${DividersAndCardBorders}`,
      [theme.breakpoints.down('xs')]: {
        height: (props: NavBarStyleProps) =>
          `${MOBILE_APP_BAR_HEIGHT + (props.showBanner ? BANNER_HEIGHT : 0)}px`,
      },
      [theme.breakpoints.up('md')]: {
        height: (props: NavBarStyleProps) =>
          `${DESKTOP_APP_BAR_HEIGHT +
            (props.showBanner ? BANNER_HEIGHT : 0)}px`,
        paddingLeft: drawerWidth,
      },
    },
    toolBar: {
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0, 4.5, 0, 4.5),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0, 2),
      },
    },
    appbarBorderBottom: {
      // when channel content toolbar is hidden
      // we want to remove the common header border bottom
      borderBottom: 0,
    },
    toolBarPadding: {
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(
          0,
          4.5,
          0,
          0, // removing toolbar left padding so that it's child element can stretch to full width
        ),
      },
    },
  }),
);

const Navbar: React.FC<NavbarContentProps> = ({
  title,
  titleIcon,
  user,
  onOpenSidebarMobile,
  showSearchbar,
  BreadCrumbComponent,
  showBreadCrumb,
  showNavbarAuthActions,
  headerContent,
}) => {
  const bannerOptions = useSelector(
    (state: RootState) => state.ui.bannerOptions,
  );
  const { channelsContentToolbarHidden } = React.useContext(
    ChannelsPageLayoutHeaderContext,
  );

  const classes = useStyles({
    showBanner: Boolean(bannerOptions),
  });

  return (
    <AppBar
      position="fixed"
      className={classNames(classes.appBar, {
        [classes.appbarBorderBottom]: channelsContentToolbarHidden,
      })}
    >
      {bannerOptions ? <Banner {...bannerOptions} /> : null}
      <Toolbar
        className={classNames(classes.toolBar, {
          [classes.toolBarPadding]: channelsContentToolbarHidden,
        })}
      >
        <Grid container>
          {headerContent || (
            <NavbarContent
              title={title}
              titleIcon={titleIcon}
              user={user}
              onOpenSidebarMobile={onOpenSidebarMobile}
              showSearchbar={showSearchbar}
              BreadCrumbComponent={BreadCrumbComponent}
              showBreadCrumb={showBreadCrumb}
              showNavbarAuthActions={showNavbarAuthActions}
            />
          )}
        </Grid>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
